import { useQuery } from '@apollo/client/react'
import { ReactElement, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { Pagination } from 'swiper/modules'
import { SwiperSlide } from 'swiper/react'

import { LmfAnalyticsProvider, LoadingSpinner, ScreenReaderOnly } from '@syconium/little-miss-figgy'
import { ViewportSizes, useIsViewport } from '@syconium/little-miss-figgy'

import { gql } from '../../../../../__generated__/graphql/catalog'
import { useFetchNostoPlacementData } from '../../../../../app/_hooks/useFetchNostoPlacementData'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { isValidNostoPlacementId } from '../../../../../types/nosto'

import { CarouselControls } from './CarouselControls'
import { Body, LoadingSpinnerWrapper, SwiperContainer, Title } from './styles'
import { UpsellsCarouselContext } from './UpsellsCarouselContext'
import { UpsellTile } from './UpsellTile'

import type { Swiper as SwiperType } from 'swiper'

export const FETCH_UPSELL_CONFIGURATION = gql(`
  query FetchUpsellConfiguration($regionId: String!) {
    siteMessages(regionId: $regionId) {
      miniCartMessages {
        id
        upsellSkus
        nostoUpsellId
      }
    }
  }
`)

export const FETCH_UPSELL_PRODUCTS = gql(`
  query GetVariantsForCartUpsell($skusForVariantGroups: [String!]) {
    productCatalogVariants(skus: $skusForVariantGroups) {
      ...UpsellTileVariant
    }
  }
`)

export const Upsells = (): ReactElement | null => {
  const {
    cart: {
      beforeYouGo: beforeYouGoText,
      upsell: {
        accessibility: { slideChanged: slideChangedText, carouselLabel },
      },
    },
    magnolia: {
      general: { loading: loadingText },
    },
  } = useTranslation()
  const { region } = useLocalization()
  const isMediumOrLargeViewport = useIsViewport([ViewportSizes.medium, ViewportSizes.large])

  const swiperRef = useRef<SwiperType | undefined>(undefined)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slideAnnouncement, setSlideAnnouncement] = useState('')

  const {
    data: upsellConfigurationData,
    loading: upsellConfigurationLoading,
    error: upsellConfigurationError,
  } = useQuery(FETCH_UPSELL_CONFIGURATION, {
    variables: {
      regionId: region.id,
    },
  })

  const { nostoUpsellId: requestedNostoPlacementId, upsellSkus: defaultUpsellSkus } =
    upsellConfigurationData?.siteMessages?.miniCartMessages ?? {}

  const nostoEnabled = !!requestedNostoPlacementId

  const defaultNostoPlacementId = 'cartpage-nosto-4'
  const nostoPlacementId = isValidNostoPlacementId(requestedNostoPlacementId)
    ? requestedNostoPlacementId
    : defaultNostoPlacementId

  const {
    data: { skusForVariantGroups: nostoUpsellSkus },
    status: nostoStatus,
  } = useFetchNostoPlacementData(nostoPlacementId)

  const skusForVariantGroups = useMemo(() => {
    return (nostoEnabled ? nostoUpsellSkus : []).concat(defaultUpsellSkus ?? []).slice(0, 3)
  }, [defaultUpsellSkus, nostoEnabled, nostoUpsellSkus])

  const {
    data,
    error: productsError,
    loading: productsLoading,
  } = useQuery(FETCH_UPSELL_PRODUCTS, {
    skip: skusForVariantGroups.length === 0,
    variables: { skusForVariantGroups },
  })

  const error = useMemo(() => {
    return !!upsellConfigurationError || !!productsError || nostoStatus === 'rejected'
  }, [nostoStatus, productsError, upsellConfigurationError])

  const loading = useMemo(() => {
    return (
      upsellConfigurationLoading || productsLoading || (nostoEnabled && nostoStatus === 'pending')
    )
  }, [nostoEnabled, nostoStatus, productsLoading, upsellConfigurationLoading])

  const variants = data?.productCatalogVariants ?? []
  const isBeginning = currentSlide === 0
  const isEnd = currentSlide === variants.length - 1

  // Announce the slide the user lands on, debounced so a fast swipe or held
  // arrow doesn't queue an announcement for every slide it passes through. The
  // ref guard skips the initial settle so opening the cart stays quiet.
  const lastAnnouncedSlide = useRef(currentSlide)
  useDebounce(
    () => {
      if (currentSlide === lastAnnouncedSlide.current) return
      lastAnnouncedSlide.current = currentSlide
      const activeVariant = variants[currentSlide]
      if (!activeVariant) return
      setSlideAnnouncement(
        slideChangedText
          .replace('{{product}}', activeVariant.group.title)
          .replace('{{current}}', String(currentSlide + 1))
          .replace('{{total}}', String(variants.length))
      )
    },
    150,
    [currentSlide]
  )

  if (!loading && !error && variants.length === 0) return null

  // TODO: Handle errors
  // Why do we never handle errors? -_-

  return (
    <Body>
      <Title>{beforeYouGoText}</Title>
      {loading ? (
        <LoadingSpinnerWrapper>
          <LoadingSpinner label={loadingText} size='24px' />
        </LoadingSpinnerWrapper>
      ) : (
        <UpsellsCarouselContext.Provider value={{ currentSlide, isBeginning, isEnd, swiperRef }}>
          <SwiperContainer
            modules={[Pagination]}
            loop={false}
            slidesPerView={1}
            allowTouchMove={!isMediumOrLargeViewport}
            pagination={{ clickable: true }}
            onBeforeInit={swiper => {
              swiperRef.current = swiper
            }}
            onSlideChange={swiper => {
              setCurrentSlide(swiper.activeIndex)
            }}
            role='group'
            aria-roledescription='carousel'
            aria-label={carouselLabel}
          >
            {variants.map((variant, index) => (
              <SwiperSlide key={variant.id}>
                <LmfAnalyticsProvider title='Upsells Slide'>
                  <UpsellTile
                    variant={variant}
                    index={index}
                    nostoPlacementId={nostoEnabled ? nostoPlacementId : undefined}
                  />
                </LmfAnalyticsProvider>
              </SwiperSlide>
            ))}
          </SwiperContainer>
          <CarouselControls />
          {/* Polite (not assertive) so a slide change doesn't interrupt whatever the screen reader is currently reading, the announcement queues instead. */}
          <ScreenReaderOnly role='status' aria-live='polite' aria-atomic='true'>
            {slideAnnouncement}
          </ScreenReaderOnly>
        </UpsellsCarouselContext.Provider>
      )}
    </Body>
  )
}
